import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import * as authService from '../../services/authService'
import {
  REWARD_FULFILLMENT_LABELS,
  getMyRewardRedemptions,
  type RewardRedemptionHistoryItem,
} from '../../services/pointsService'
import { HistoryCollapseCard, type HistoryDetailRow } from '../history/HistoryCollapseCard'
import '../history/historyLayout.css'
import './pointsLayout.css'

const PAGE_SIZE = 20

const formatNumber = (value: number | null | undefined, digits = 0) =>
  Number(value || 0).toLocaleString('th-TH', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('th-TH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const buildRows = (item: RewardRedemptionHistoryItem): HistoryDetailRow[] => {
  const shipping = item.shippingInfo || {}
  const rows: HistoryDetailRow[] = [
    { label: 'รางวัล', value: item.rewardName },
    { label: 'ประเภท', value: item.rewardType === 'credit' ? 'เครดิต' : 'ของรางวัล' },
    { label: 'สถานะ', value: item.status === 'failed' ? 'ไม่สำเร็จ' : 'สำเร็จ' },
    {
      label: 'สถานะจัดส่ง',
      value: REWARD_FULFILLMENT_LABELS[item.fulfillmentStatus] || item.fulfillmentStatus,
    },
    { label: 'คะแนนที่ใช้', value: `${formatNumber(item.pointsCost)} คะแนน` },
  ]

  if (item.rewardType === 'credit') {
    rows.push({ label: 'เครดิตที่ได้', value: `${formatNumber(item.creditAmount, 2)} บาท`, tone: 'positive' })
  }

  if (item.beforePoints != null && item.afterPoints != null) {
    rows.push({
      label: 'คะแนนคงเหลือ',
      value: `${formatNumber(item.beforePoints)} → ${formatNumber(item.afterPoints)}`,
    })
  }

  if (item.rewardType === 'item') {
    rows.push(
      { label: 'ชื่อผู้รับ', value: shipping.name || '-' },
      { label: 'เบอร์ติดต่อ', value: shipping.phone || '-' },
      { label: 'ที่อยู่/หมายเหตุ', value: shipping.address || '-', tone: 'muted' }
    )
  }

  rows.push(
    { label: 'เวลาทำรายการ', value: formatDateTime(item.createdAt) },
    { label: 'อัปเดตล่าสุด', value: formatDateTime(item.updatedAt) }
  )

  if (item.note) {
    rows.push({ label: 'หมายเหตุจากระบบ', value: item.note, tone: 'muted' })
  }

  return rows
}

const PointsRewardHistoryPage: React.FC = () => {
  const [items, setItems] = useState<RewardRedemptionHistoryItem[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadHistory = useCallback(async (pageNum = 1) => {
    const currentUser = authService.getCurrentUser()
    const tenantId = currentUser?.tenantId
    const userId = currentUser?.id
    if (!tenantId || !userId) {
      setItems([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await getMyRewardRedemptions({ tenantId, userId, page: pageNum, limit: PAGE_SIZE })
      setItems(response.data || [])
      setPage(response.pagination?.page || pageNum)
      setTotalPages(Math.max(1, response.pagination?.totalPages || 1))
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'โหลดประวัติไม่สำเร็จ')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadHistory(1)
  }, [loadHistory])

  const handleCopyRef = async (refId: string) => {
    try {
      await navigator.clipboard.writeText(refId)
    } catch {
      // ignore clipboard errors
    }
  }

  return (
    <div className="htd-pts__stack">
      <h2 className="htd-pts__page-title">ประวัติแลกรางวัล</h2>

      <div className="htd-pts__panel reward-history-panel">
        <div className="reward-history-hints mb-3">
          <div className="reward-history-hint">
            <span className="reward-history-hint__icon">
              <i className="fas fa-history" aria-hidden="true" />
            </span>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>ประวัติแลกรางวัล</div>
              <div style={{ color: 'rgba(186,230,253,0.75)', fontSize: '0.8rem' }}>
                ดูรายการย้อนหลัง คะแนนที่ใช้ และสถานะการจัดส่ง
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="reward-history-state">
            <div className="reward-history-state__icon">
              <i className="fas fa-spinner fa-spin" aria-hidden="true" />
            </div>
            <p className="mb-0" style={{ color: '#fff', fontWeight: 700 }}>กำลังโหลดประวัติ...</p>
          </div>
        )}

        {!loading && error && (
          <div className="reward-history-state">
            <div className="reward-history-state__icon">
              <i className="fas fa-exclamation-triangle" aria-hidden="true" />
            </div>
            <p className="mb-3" style={{ color: '#fca5a5', fontWeight: 700 }}>{error}</p>
            <button
              type="button"
              className="htd-pts__pkg-btn"
              style={{ width: 'auto', borderRadius: 999, padding: '0.6rem 1.2rem', margin: '0 auto' }}
              onClick={() => void loadHistory(page)}
            >
              ลองโหลดอีกครั้ง
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="reward-history-state">
            <div className="reward-history-state__icon">
              <i className="fas fa-gift" aria-hidden="true" />
            </div>
            <h5 className="mb-2" style={{ color: '#fff', fontWeight: 800 }}>ยังไม่มีประวัติการแลกรางวัล</h5>
            <p className="mb-4" style={{ color: 'rgba(203,213,225,0.85)', fontSize: '0.86rem' }}>
              เมื่อคุณแลกของรางวัลหรือเครดิตด้วยคะแนน รายการจะแสดงที่นี่พร้อมสถานะการจัดส่ง
            </p>
            <Link
              to="/points/rewards"
              className="htd-pts__pkg-btn text-decoration-none d-inline-flex"
              style={{ width: 'auto', borderRadius: 999, padding: '0.65rem 1.2rem' }}
            >
              <i className="fas fa-arrow-right me-2" />
              ไปหน้าแลกรางวัล
            </Link>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="reward-history-summary px-3 py-2 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <span style={{ fontSize: '0.85rem', color: '#e0f2fe' }}>
                <i className="fas fa-list-ul me-2" aria-hidden="true" />
                พบ {items.length} รายการ{totalPages > 1 ? ` (หน้า ${page}/${totalPages})` : ''}
              </span>
              <span style={{ fontSize: '0.8rem', color: 'rgba(186,230,253,0.75)' }}>แตะการ์ดเพื่อดูรายละเอียด</span>
            </div>
            <div className="d-flex flex-column gap-2">
            {items.map((item) => {
              const refId = item.redemptionId
              const fulfillmentLabel = REWARD_FULFILLMENT_LABELS[item.fulfillmentStatus] || item.fulfillmentStatus
              const leftIcon =
                item.status === 'failed'
                  ? 'reject'
                  : item.fulfillmentStatus === 'delivered' || item.rewardType === 'credit'
                    ? 'success'
                    : 'pending'

              return (
                <HistoryCollapseCard
                  key={refId}
                  id={refId}
                  leftIcon={leftIcon}
                  refId={refId}
                  onCopyRef={() => void handleCopyRef(refId)}
                  header={(
                    <div className="w-100 pe-md-4">
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>{item.rewardName}</div>
                      <small style={{ color: 'rgba(186,230,253,0.75)', display: 'block' }}>
                        ใช้ {formatNumber(item.pointsCost)} คะแนน · {formatDateTime(item.createdAt)}
                      </small>
                      <small style={{ color: 'rgba(186,230,253,0.75)', display: 'block' }}>
                        {item.rewardType === 'credit' ? 'รางวัลเครดิต' : 'รางวัลของ'} · {fulfillmentLabel}
                      </small>
                    </div>
                  )}
                  rows={buildRows(item)}
                />
              )
            })}
            </div>
          </>
        )}

        {!loading && !error && totalPages > 1 && (
          <div className="d-flex align-items-center justify-content-between gap-2 mt-3">
            <button
              type="button"
              className="htd-pts__pkg-btn"
              style={{ width: 'auto', borderRadius: 999, padding: '0.55rem 1rem', background: 'rgba(255,255,255,0.08)' }}
              disabled={page <= 1}
              onClick={() => void loadHistory(page - 1)}
            >
              ก่อนหน้า
            </button>
            <div style={{ color: 'rgba(186,230,253,0.85)', fontWeight: 700 }}>หน้า {page} / {totalPages}</div>
            <button
              type="button"
              className="htd-pts__pkg-btn"
              style={{ width: 'auto', borderRadius: 999, padding: '0.55rem 1rem', background: 'rgba(255,255,255,0.08)' }}
              disabled={page >= totalPages}
              onClick={() => void loadHistory(page + 1)}
            >
              ถัดไป
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default PointsRewardHistoryPage
